/**
 * @fileoverview Unified Search module for the Curtis Institute of Music WordPress theme.
 * This module manages the unified search results page functionality, including:
 * - Post type filtering with buttons
 * - Loading and displaying unified search results
 * - Implementing "load more" pagination
 * - Managing URL parameters for filters
 * - Handling AJAX requests to fetch filtered results
 *
 * @module search-unified
 */

/**
 * Interface for DOM elements used by the unified search module
 * @interface Elements
 */
type Elements = {
  /** Main search contents container */
  module: HTMLElement | null;
  /** Filter navigation container */
  filterNav: HTMLElement | null;
  /** All filter buttons */
  filterButtons: NodeListOf<HTMLButtonElement>;
  /** Results list container */
  resultsList: HTMLUListElement | null;
  /** Load more button */
  loadMoreButton: HTMLButtonElement | null;
  /** Search input field */
  searchInput: HTMLInputElement | null;
}

/**
 * Unified Search module interface
 * @interface SearchUnified
 */
type SearchUnified = {
  /** Number of posts to load per page */
  postsPerPage: number;
  /** Current filter */
  currentFilter: string;
  /** Current search query */
  currentQuery: string;
  /** Current page number */
  currentPage: number;
  /** DOM elements used by the search module */
  elements: Elements;
  /** Initialization method */
  init: () => void;
  /** Event binding method */
  bindEvents: () => void;
  /** Handles filter button clicks */
  handleFilterClick: (event: Event) => void;
  /** Handles load more button click */
  handleLoadMoreClick: () => void;
  /** Fetches search results via AJAX */
  getResults: (page: number, filter: string, append?: boolean) => void;
  /** Updates URL with current filter */
  updateURL: (filter: string) => void;
  /** Gets current filter from URL */
  getFilterFromURL: () => string;
  /** Updates the active state of filter buttons */
  updateFilterButtonStates: () => void;
  /** Updates the filter counts displayed on filter buttons */
  updateFilterCounts: (counts: { [key: string]: number }) => void;
};

/**
 * Unified Search module implementation that manages search results and filtering
 * @type {SearchUnified}
 */
const searchUnified: SearchUnified = {
  /**
   * Number of posts to load per page for pagination
   * @type {number}
   */
  postsPerPage: 10,

  /**
   * Current active filter
   * @type {string}
   */
  currentFilter: 'all',

  /**
   * Current search query
   * @type {string}
   */
  currentQuery: '',

  /**
   * Current page number
   * @type {number}
   */
  currentPage: 1,

  /**
   * DOM elements used by the search module
   * @property {HTMLElement|null} module - Main search contents container
   * @property {HTMLElement|null} filterNav - Filter navigation container
   * @property {NodeListOf<HTMLButtonElement>} filterButtons - All filter buttons
   * @property {HTMLUListElement|null} resultsList - Results list container
   * @property {HTMLButtonElement|null} loadMoreButton - Load more button
   * @property {HTMLInputElement|null} searchInput - Search input field
   */
  elements: {
    module: document.querySelector('.site-search__contents'),
    filterNav: document.querySelector('.site-search__filter-nav'),
    filterButtons: document.querySelectorAll('.site-search__filter-btn'),
    resultsList: document.querySelector('.site-search__unified-results .site-search__results'),
    loadMoreButton: document.querySelector('[data-action="load-more-unified"]'),
    searchInput: document.querySelector('#search-results-page') as HTMLInputElement,
  },

  /**
   * Initializes the unified search module
   * Sets up event bindings and initializes autocomplete if on search results page
   *
   * @returns {void}
   * @example
   * searchUnified.init(); // Initializes unified search functionality
   */
  init() {
    if (!this.elements.module) return;

    // Get initial filter from URL or data attribute
    this.currentFilter = this.getFilterFromURL();

    // Get search query from input field
    if (this.elements.searchInput) {
      this.currentQuery = this.elements.searchInput.value || '';
    }

    // Get current page from load more button if exists
    if (this.elements.loadMoreButton) {
      this.currentPage = parseInt(this.elements.loadMoreButton.dataset.page || '1', 10);
    }

    this.bindEvents();

    // Set initial active state on filter buttons
    this.updateFilterButtonStates();
  },

  /**
   * Binds click events to filter buttons and load more button
   * Sets up event listeners for filtering and pagination functionality
   *
   * @returns {void}
   */
  bindEvents() {
    // Bind filter button clicks
    this.elements.filterButtons?.forEach(button => {
      button.addEventListener('click', (e) => this.handleFilterClick(e));
    });

    // Bind load more button click
    this.elements.loadMoreButton?.addEventListener('click', () => this.handleLoadMoreClick());
  },

  /**
   * Handles filter button click events
   * Updates the current filter, fetches new results, and updates URL
   *
   * @param {Event} event - The click event
   * @returns {void}
   * @example
   * // User clicks "Events" filter button
   * searchUnified.handleFilterClick(event); // Filters results to show only events
   */
  handleFilterClick(event) {
    const button = event.currentTarget as HTMLButtonElement;
    const filter = button.dataset.filter || 'all';

    // Don't do anything if clicking the already active filter
    if (filter === this.currentFilter) {
      return;
    }

    // Update current filter
    this.currentFilter = filter;
    this.currentPage = 1; // Reset to first page when changing filters

    // Update button states
    this.updateFilterButtonStates();

    // Update URL
    this.updateURL(filter);

    // Fetch new results
    this.getResults(1, filter, false);
  },

  /**
   * Updates the active state of filter buttons
   *
   * @returns {void}
   */
  updateFilterButtonStates() {
    this.elements.filterButtons?.forEach(button => {
      const isActive = button.dataset.filter === this.currentFilter;
      if (isActive) {
        button.classList.add('site-search__filter-btn--active');
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.classList.remove('site-search__filter-btn--active');
        button.setAttribute('aria-pressed', 'false');
      }
    });
  },

  /**
   * Handles the load more button click
   * Increments the page number and fetches additional results
   *
   * @returns {void}
   * @example
   * // User clicks "Load More" button
   * searchUnified.handleLoadMoreClick(); // Loads next page of results
   */
  handleLoadMoreClick() {
    const button = this.elements.loadMoreButton;
    if (!button) return;

    const newPage = parseInt(button.dataset.page || '1', 10) + 1;
    this.getResults(newPage, this.currentFilter, true);
  },

  /**
   * Fetches search results via AJAX for the unified search
   * Handles pagination, updates the DOM with results, and manages button visibility
   *
   * @param {number} page - The page number to fetch (1-based)
   * @param {string} filter - The filter to apply ('all', 'events', 'people', or 'posts')
   * @param {boolean} append - Whether to append results or replace them
   * @returns {void}
   * @example
   * // Fetch page 2 of event results
   * searchUnified.getResults(2, 'events', true);
   *
   * @example
   * // Fetch first page of all results (replace existing)
   * searchUnified.getResults(1, 'all', false);
   */
  getResults(page, filter, append = false) {
    const button = this.elements.loadMoreButton;
    const resultsList = this.elements.resultsList;

    // Show loading state
    if (button) {
      button.textContent = 'Loading...';
      button.disabled = true;
    }

    window.jQuery.ajax({
      method: 'POST',
      url: window.curtis.ajaxurl,
      data: {
        action: 'action_get_unified_search',
        query: this.currentQuery,
        filter: filter,
        page: page
      },
    })
    .success((success: string) => {
      const data = JSON.parse(success);

      if (resultsList && data.results) {
        if (append) {
          // Append new results to existing ones
          resultsList.innerHTML = `${resultsList.innerHTML}${data.results}`;
        } else {
          // Replace existing results
          resultsList.innerHTML = data.results;
        }
      } else if (resultsList && !data.results) {
        if (!append) {
          resultsList.innerHTML = '<li class="site-search__no-results">No results found.</li>';
        }
        button?.classList.add('hidden');
      }

      // Update the page number
      if (button) {
        button.setAttribute('data-page', `${page}`);
        button.textContent = 'Load More';
        button.disabled = false;
      }

      // Hide the load button if we have all results
      if (button && (data.hasMore === false || page >= data.totalPages)) {
        button.classList.add('hidden');
      } else if (button) {
        button.classList.remove('hidden');
      }

      // Update result counts in filter buttons if provided
      if (data.filterCounts) {
        this.updateFilterCounts(data.filterCounts);
      }
    })
    .fail((_jqXHR: JQuery.jqXHR, _textStatus: string, errorThrown: string) => {
      console.error('[SearchUnified] AJAX error:', errorThrown);

      if (button) {
        button.textContent = 'Load More';
        button.disabled = false;
      }

      if (resultsList && !append) {
        resultsList.innerHTML = '<li class="site-search__no-results">An error occurred while loading results.</li>';
      }
    });
  },

  /**
   * Updates the filter counts displayed on filter buttons
   *
   * @param {Object} counts - Object with filter counts
   * @returns {void}
   */
  updateFilterCounts(counts: { [key: string]: number }) {
    this.elements.filterButtons?.forEach(button => {
      const filter = button.dataset.filter;
      if (filter && counts[filter] !== undefined) {
        const countSpan = button.querySelector('.site-search__filter-count');
        if (countSpan) {
          countSpan.textContent = `(${counts[filter]})`;
        } else if (counts[filter] > 0) {
          // Create count span if it doesn't exist
          const newCountSpan = document.createElement('span');
          newCountSpan.className = 'site-search__filter-count';
          newCountSpan.textContent = `(${counts[filter]})`;
          button.appendChild(newCountSpan);
        }
      }
    });
  },

  /**
   * Updates the URL with the current filter parameter
   * Uses the History API to update without page reload
   *
   * @param {string} filter - The filter to set in the URL
   * @returns {void}
   * @example
   * searchUnified.updateURL('events'); // Updates URL to include ?filter=events
   */
  updateURL(filter) {
    const url = new URL(window.location.href);

    if (filter === 'all') {
      url.searchParams.delete('filter');
    } else {
      url.searchParams.set('filter', filter);
    }

    // Reset page parameter when changing filters
    url.searchParams.delete('pg');

    // Update the URL without reloading the page
    window.history.pushState({ filter }, '', url.toString());
  },

  /**
   * Gets the current filter from the URL parameters
   * Falls back to 'all' if no filter is specified
   *
   * @returns {string} The current filter value
   * @example
   * // URL: /search?s=test&filter=events
   * const filter = searchUnified.getFilterFromURL(); // Returns 'events'
   */
  getFilterFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get('filter') || 'all';

    // Validate filter value
    const validFilters = [ 'all', 'events', 'people', 'posts' ];
    return validFilters.includes(filter) ? filter : 'all';
  }
};

export default searchUnified;
